import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip
} from '@mui/material';
import { format, differenceInDays } from 'date-fns';
import { Performance } from '../../types/mongodb';
import axiosInstance from '../../utils/axiosConfig'; // Adjust import path as needed

interface PerformanceStaff {
  _id: string;
  name: string;
  role: string; 
  staffId: string;
  assignedRole: string;
  fee: number;
}

interface MarketingAsset {
  filename: string;
  url: string;
  type: 'pdf' | 'image' | 'document';
  description?: string;
  uploadedAt: Date;
}

interface ResourceData {
  _id: string;
  marketingAssets?: MarketingAsset[];
  mainImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
  };
  mainPosterImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    dimensions?: string;
    format?: string;
  };
  additionalPromoImages?: Array<{
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    description?: string;
  }>;
}

interface PerformanceCardProps {
  performance: Performance;
  bandName: string;
  bandImage?: string;
  staffMember?: PerformanceStaff;
}

const PerformanceCard: React.FC<PerformanceCardProps> = ({ 
  performance, 
  bandName, 
  bandImage, 
  staffMember 
}) => {
  const navigate = useNavigate();
  const [resourceData, setResourceData] = useState<ResourceData | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [loadingResource, setLoadingResource] = useState(false);
  
  const daysUntil = differenceInDays(new Date(performance.startTime), new Date());

  // Fetch resource data for this performance - EXACT SAME LOGIC
  useEffect(() => {
    const fetchResourceData = async () => {
      if (!performance._id) return;
      
      setLoadingResource(true);
      try {
        // Use the existing endpoint from your resources routes
        const response = await axiosInstance.get(`/api/resources/performance/${performance._id}`);
        
        if (response.data && response.data.exists !== false) {
          // Only process if resource actually exists (not just a template)
          const resource = response.data;
          setResourceData(resource);
          
          // Check for marketing images and set background
          const marketingImage = getMarketingImage(resource);
          setBackgroundImage(marketingImage);
        }
      } catch (error) {
        // Resource might not exist yet - that's okay
        console.log('No resource found for performance:', performance._id);
      } finally {
        setLoadingResource(false);
      }
    };

    fetchResourceData();
  }, [performance._id]);

  // Helper function to get the best marketing image for background - EXACT SAME LOGIC
  const getMarketingImage = (resource: ResourceData): string | null => {
    let imageUrl: string | null = null;

    // Priority 1: Marketing assets with type 'image'
    if (resource.marketingAssets && resource.marketingAssets.length > 0) {
      const imageAsset = resource.marketingAssets.find(asset => asset.type === 'image');
      if (imageAsset) {
        imageUrl = imageAsset.url;
      }
    }

    // Priority 2: Main poster image
    if (!imageUrl && resource.mainPosterImage?.url) {
      imageUrl = resource.mainPosterImage.url;
    }

    // Priority 3: Main image
    if (!imageUrl && resource.mainImage?.url) {
      imageUrl = resource.mainImage.url;
    }

    // Priority 4: First additional promo image
    if (!imageUrl && resource.additionalPromoImages && resource.additionalPromoImages.length > 0) {
      imageUrl = resource.additionalPromoImages[0].url;
    }

    // URL encode the image URL to handle spaces and special characters
    if (imageUrl) {
      // Split the URL to only encode the filename part after the last slash
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      const encodedFilename = encodeURIComponent(filename);
      urlParts[urlParts.length - 1] = encodedFilename;
      return urlParts.join('/');
    }

    return null;
  };

  // Use fetched background image, fallback to bandImage prop, then to none
  const finalBackgroundImage = backgroundImage || bandImage;

  return (
    <Card 
      onClick={() => navigate(`/admin/performances/${performance._id}`)}
      sx={{ 
        mb: 2,
        position: 'relative',
        minHeight: 200,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.02)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: finalBackgroundImage ? `url(${finalBackgroundImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(to bottom, 
            rgba(0,0,0,0.2) 0%, 
            rgba(0,0,0,0.7) 50%, 
            rgba(0,0,0,0.9) 100%)`,
          zIndex: 1
        }
      }}
    >
      <CardContent 
        sx={{ 
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          color: 'white'
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 1,
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
          }}
        >
          {bandName}
        </Typography>
        <Typography 
          variant="body1"
          sx={{ 
            mb: 2,
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
          }}
        >
          {format(new Date(performance.startTime), 'PPP p')}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap'
        }}>
          <Chip
            label={`${daysUntil} days until show`}
            color="primary"
            size="small"
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              '& .MuiChip-label': {
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }
            }}
          />
          {staffMember && (
            <Chip
              label={staffMember.assignedRole}
              color="secondary"
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                '& .MuiChip-label': {
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                }
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PerformanceCard;